export type Lang = 'en' | 'pt' | 'de' | 'fr' | 'es' | 'uk';

export const translations: Record<Lang, Record<string, Record<string, string>>> = {
  // ─────────────────────────────────────────────
  // ENGLISH
  // ─────────────────────────────────────────────
  en: {
    lp: {
      menuFeatures: 'Features',
      getStarted: 'Get Started',
      heroEyebrow: 'Smart TV media platform',
      heroH1a: 'Smarter TV control',
      heroH1b: 'for a next-gen',
      heroH1c: 'viewing experience.',
      heroLead: 'Organize your catalog, control your TV from your phone and enjoy safer, smarter entertainment.',
      heroTry: 'Try free for 7 days →',
      heroPlans: 'See plans',
      trust1: 'Quick activation',
      trust2: 'Works on Smart TVs',
      trust3: 'Secure & private',
      compatTitle: 'Compatible with leading Smart TV platforms',
      moreName: '& More',
      moreSub: 'Platforms',
      featKicker: 'Powerful features',
      featTitleA: 'Everything you need,',
      featTitleB: 'built for better TV.',
      learn: 'Learn more →',
      f1t: 'Control from your phone',
      f1d: 'Use your smartphone as a remote. Fast, intuitive and always at hand.',
      f2t: 'Safer for your family',
      f2d: 'PIN protection, content filters and parental controls to keep everyone safe.',
      f3t: 'Powerful playback',
      f3d: 'Smooth performance, multi-format support and premium streaming quality.',
      f4t: 'Organized catalog',
      f4d: 'Import, sort and manage your content with beautiful categories.',
      stat1: 'Compatible platforms',
      stat2: 'Languages supported',
      stat3: 'Average quick activation',
      stat4: 'Premium support',
      setupKicker: 'Easy setup',
      setupTitle: 'Get started in 3 simple steps',
      step1t: 'Activate your TV',
      step1d: 'Scan the QR code or enter the activation code on your TV to connect in seconds.',
      step2t: 'Add your content',
      step2d: 'Import your playlists or media sources and organize them your way.',
      step3t: 'Watch and control',
      step3d: 'Enjoy your content and control everything from your phone, anytime, anywhere.',
      priceKicker: 'Pricing',
      priceTitle: 'Choose the plan that fits you best',
      priceSub: 'One platform. All your entertainment. Your way.',
      annualName: 'Annual',
      annualDesc: '12-month access',
      annualUnit: '/year',
      annualF1: 'Full player without restrictions',
      annualF2: 'Playlist support',
      annualF3: 'Parental controls',
      annualF4: 'PIN security',
      annualF5: 'Organized catalog',
      annualCta: 'Start annual plan',
      bestValue: 'Best value',
      lifeName: 'Lifetime',
      lifeDesc: 'One-time payment',
      lifeUnit: 'forever',
      lifeF1: 'Everything in Annual plan',
      lifeF2: 'Lifetime access',
      lifeF3: 'Future updates included',
      lifeF4: 'No renewal',
      lifeF5: 'Priority support',
      lifeCta: 'Get lifetime access',
      ben1: '7-day free trial',
      ben2: 'Secure payment',
      ben3: 'Cancel anytime',
      ctaTitle: 'The future of TV is in your hands.',
      ctaSub: 'Join thousands of users enjoying smarter, safer and more powerful entertainment with MoovaTV.',
      ctaBtn: 'Try MoovaTV free for 7 days →',
      pnpKicker: 'Player, not provider',
      pnpTitleA: 'We sell the player.',
      pnpTitleB: 'Never the content.',
      pnpLead: 'MoovaTV ships with no channels, no playlists and no content subscriptions. You bring your own sources; we make them look and feel premium on your TV.',
      pnpYesT: 'What you get',
      pnpYes1: 'A premium player for your own playlists and media sources',
      pnpYes2: 'EPG, favourites, parental controls and multi-device sync',
      pnpYes3: 'Phone-as-remote on Tizen, webOS, Roku, Google TV and more',
      pnpNoT: 'What we never sell',
      pnpNo1: 'No TV channels or live streams',
      pnpNo2: 'No playlists, packages or content subscriptions',
      pnpNo3: 'No content hosting: your sources stay yours',
      footTagline: 'Smart TV control for a next-gen viewing experience.',
      footProduct: 'Product',
      footFeatures: 'Features',
      footActivation: 'Activation',
      footPlaylist: 'Playlist',
      footDevices: 'Devices',
      footCompany: 'Company',
      footContact: 'Contact',
      footAbout: 'About Us',
      footReseller: 'Reseller',
      footBecome: 'Become a Reseller',
      footLogin: 'Reseller Login',
      footLegal: 'Legal',
      footPrivacy: 'Privacy Policy',
      footTerms: 'Terms of Use',
      footCopy: '© 2026 MoovaTV. All rights reserved. · Secure · Private · Reliable',
    },
    nav: {
      home: 'Home',
      activation: 'Activation',
      playlist: 'Playlist',
      reseller: 'Reseller',
      pricing: 'Pricing',
      skipToContent: 'Skip to content',
      primary: 'Primary',
      language: 'Language',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
    },
    footer: {
      tagline: 'More control, organization and security for your TV experience.',
      product: 'Product',
      partners: 'Partners',
      becomeReseller: 'Become a Reseller',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
      copyright: 'All rights reserved.',
      disclaimer:
        'MoovaTV is exclusively a media player. We do not sell, distribute or provide playlists, channels or content of any kind.',
    },
    home: {
      metaTitle: 'MoovaTV — Media player for Smart TVs with phone control',
      metaDesc: 'Organize your catalog, control the TV from your phone and watch with more security on compatible Smart TVs. Free 7-day trial.',
      heroTitle1: 'Take control of your TV with a smarter',
      heroTitle2: 'media player.',
      heroSub:
        'Organize your catalog, control the TV from your phone and watch with more security on compatible Smart TVs.',
      heroCta: 'Try free for 7 days',
      heroPlans: 'See plans',
      heroMicro: 'Quick activation. No credit card for free trial.',
      marqueeLabel: 'Compatible with the leading Smart TV platforms and brands.',
      featuresLabel: 'Features',
      featuresTitle: 'Everything you need to watch with more control.',
      featuresSub: 'More comfort, better organization and stronger security for your viewing experience.',
      feat1Title: 'Control everything from your phone',
      feat1Desc:
        'Browse the catalog, send to TV and control playback in real time via second screen.',
      feat2Title: 'More security for your home',
      feat2Desc:
        'Use PIN, kids mode and browsing filters for more control over content access.',
      feat3Title: 'More complete playback',
      feat3Desc:
        'Get subtitles, multiple audio tracks and adaptive quality for a more stable and comfortable experience.',
      feat4Title: 'Better organized catalog',
      feat4Desc:
        'Richer visuals with covers, synopses and info that help you find what to watch faster.',
      syncLabel: 'Second Screen',
      syncTitle1: 'Choose on your phone.',
      syncTitle2: 'Watch on TV.',
      syncSub: 'Browse with more agility, send content to TV and control everything without relying on a confusing remote.',
      syncMicro: 'Real-time sync',
      syncStep0: 'Browsing catalog',
      syncStep1: 'Selecting movie',
      syncStep2: 'Viewing details',
      syncStep3: 'Sending to TV',
      syncStep4: 'Playing on TV',
      syncStep5: 'Phone as remote',
      syncStep6: 'Paused from phone',
      remoteNowPlaying: 'Now playing',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'Paused',
      syncWatchOnTv: 'Watch on TV',
      syncSentToTv: 'Sent to TV',
      syncTvWaiting: 'Waiting',
      numbersSub: 'Wide compatibility and quick setup to get started without hassle.',
      stepsTitle: 'Get started in minutes',
      stepsSub: 'Activate your TV, add your content and control everything from your phone.',
      step: 'Step',
      step1Title: 'Activate your TV',
      step1Desc:
        'Scan the QR code on screen or enter the activation code. The process takes less than 30 seconds.',
      step2Title: 'Add your content',
      step2Desc:
        'Import your playlist and keep everything organized by category, quality and genre.',
      step3Title: 'Watch and control',
      step3Desc:
        'Browse the catalog on your phone, send to TV and manage playback in real time.',
      pricingTitle: 'Choose the right plan for the player',
      pricingSub:
        "You pay for access to the player and platform features. MoovaTV does not sell playlists, channels or content.",
      planFreeName: 'Free Trial',
      planFreeInfo: 'Try the platform for 7 days',
      planFreePrice: 'Free',
      planFreeFeat1: 'Full access during trial period',
      planFreeFeat2: 'All features unlocked',
      planFreeFeat3: 'No credit card',
      planFreeCta: 'Start free',
      planAnnualName: 'Annual',
      planAnnualInfo: '12-month access',
      planAnnualDetail: '/ year',
      planAnnualFeat1: 'Full player without restrictions',
      planAnnualFeat2: 'Phone control',
      planAnnualFeat3: 'PIN security',
      planAnnualFeat4: 'Organized and enriched catalog',
      planAnnualFeat5: 'Interface in 7 languages',
      planAnnualFeat6: 'Priority support',
      planAnnualCta: 'Subscribe annual',
      planLifetimeName: 'Lifetime',
      planGoldName: 'Gold Lifetime',
      planGoldInfo: 'One-time payment',
      planGoldDetail: 'forever',
      planGoldBadge: 'Best value',
      planGoldSavings: 'Best value',
      planGoldFeat1: 'Everything from Annual plan',
      planGoldFeat2: 'Lifetime player access',
      planGoldFeat3: 'No renewal',
      planGoldFeat4: 'Future updates included',
      planGoldFeat5: 'Priority support',
      planGoldCta: 'Activate now',
      ctaTitle1: 'A better TV experience',
      ctaTitle2: 'starts here.',
      ctaCta: 'Try free for 7 days',
      ctaMicro: 'Activate in minutes and try all features.',
      platforms: 'COMPATIBLE PLATFORMS',
      epgComplete: 'FULL EPG GUIDE',
      languages: 'AVAILABLE LANGUAGES',
      activation: 'QUICK ACTIVATION',
    },
    activation: {
      title: 'Activate your device',
      subtitle: 'Subscribe to unlock the Moova player on your Smart TV',
      step1: 'Enter the MAC address shown on your TV',
      step2: 'Choose a plan that suits you',
      step3: 'Subscribe — your TV activates instantly',
      planLifetime: 'Lifetime',
      planAnnual: 'Annual',
      planMonthly: 'Monthly',
      choosePlan: 'Choose your plan',
      choosePlanSub: 'Select the option that best suits you',
      macLabel: 'MAC Address',
      checking: 'Checking device...',
      found: 'Device found — choose a plan to activate',
      alreadyPaid: 'This device is already activated. No payment needed.',
      notFound: 'Not found. Install and open the app on your TV first.',
      smartTv: 'Smart TV',
      securePayment: 'Secure payment',
      instantActivation: 'Instant activation',
      acceptTerms: 'I accept the',
      termsLink: 'terms of use and privacy policy',
      processing: 'Processing...',
      activateBtn: 'Activate',
      activateDevice: 'Activate Device',
      disclaimer:
        'The app does not include playlists or channels. Set up your own playlist after activation.',
      popular: 'Popular',
      oneTime: 'One-time payment',
      perYear: 'Per year',
      perMonth: 'Per month',
      errorPayment: 'Could not generate payment link. Please try again.',
      errorGeneric: 'Error processing payment. Please try again.',
      macFound: 'Found',
      descLifetime: 'Pay once, use forever',
      descAnnual: 'Renewed annually',
      descMonthly: 'Renewed monthly',
      alreadyPaidUntil: 'This device already has an active plan until {date}. No payment needed.',
      alreadyLifetime: 'This device already has the Lifetime plan. No payment needed.',
    },
    playlist: {
      title: 'Upload Playlist',
      subtitle:
        'Load your own playlist to the app. Supports M3U, M3U8 and other sources.',
      step1: "Enter your TV device's MAC address",
      step2: 'Choose an upload method and provide the playlist',
      step3: 'Submit — the playlist syncs to your TV instantly',
      multipleFormats: 'Multiple formats',
      multipleFormatsDesc:
        'Upload via URL, file, server credentials, or direct M3U8 link.',
      epgSupport: 'EPG support',
      epgSupportDesc:
        'Add an Electronic Program Guide URL for channel schedules.',
      pinProtection: 'PIN protection',
      pinProtectionDesc:
        'Optionally protect your playlist with a 4-digit PIN.',
      macLabel: 'MAC Address',
      checking: 'Checking device...',
      found: 'Device found — you can upload your playlist',
      notFound: 'Not found. Install and open the app on your TV first.',
      playlistName: 'Playlist Name',
      playlistPlaceholder: 'My Playlist',
      uploadMethod: 'Upload method',
      urlTab: 'URL',
      fileTab: 'File',
      hostTab: 'Host',
      m3u8Tab: 'M3U8',
      m3uUrl: 'M3U URL',
      m3u8Url: 'M3U8 URL',
      m3u8Note: 'For M3U8 lists from other sources',
      m3uFile: 'M3U/M3U8 File',
      hostLabel: 'Host',
      usernameLabel: 'Username',
      passwordLabel: 'Password',
      options: 'Options',
      epgUrl: 'EPG URL (optional)',
      protectPin: 'Protect playlist with PIN',
      pinPlaceholder: '4-digit PIN',
      setDefault: 'Set as default playlist',
      uploading: 'Uploading...',
      uploadBtn: 'Upload Playlist',
      uploadSuccess: 'Playlist uploaded successfully!',
      uploadError: 'Error uploading playlist. Check the MAC and try again.',
      fileError: 'Only .m3u and .m3u8 files are supported',
    },
    reseller: {
      title: 'Become a Reseller',
      subtitle:
        'Join our partner program and start earning with MoovaTV activations.',
      step1: 'Create your reseller account',
      step2: 'Access the dashboard and generate activation codes',
      step3: 'Sell to your clients and earn commissions',
      manageClients: 'Manage your clients',
      manageClientsDesc:
        'Full dashboard to track activations, clients and earnings.',
      competitivePricing: 'Competitive pricing',
      competitivePricingDesc:
        'Buy credits in bulk at discounted prices.',
      realTimeAnalytics: 'Real-time analytics',
      realTimeAnalyticsDesc:
        'Track your revenue, active devices and growth.',
      createAccount: 'Create your account',
      fieldsRequired: 'Fields marked with * are required',
      nameLabel: 'Name',
      namePlaceholder: 'First name',
      surnameLabel: 'Surname',
      surnamePlaceholder: 'Last name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Min 6 characters',
      confirmLabel: 'Confirm',
      confirmPlaceholder: 'Repeat password',
      passwordMismatch: "Passwords don't match",
      optional: 'Optional',
      companyLabel: 'Company',
      companyPlaceholder: 'Company name',
      phoneLabel: 'Phone',
      countryLabel: 'Country',
      countryPlaceholder: 'Country',
      countrySelect: 'Select your country',
      countrySearch: 'Search country...',
      countryNone: 'No country found',
      phoneInvalid: 'Invalid phone number for the selected country',
      dialCode: 'Country code',
      partnerType: 'Partner type',
      resellerOption: 'Reseller',
      resellerDesc: 'Buy and sell activations',
      referralOption: 'Referral',
      referralDesc: 'Earn per referral',
      creatingAccount: 'Creating account...',
      createBtn: 'Create Reseller Account',
      registerSuccess: 'Registration successful! Redirecting...',
      registerError: 'Registration failed. Please try again.',
    },
    pricing: {
      title: 'Simple, transparent pricing',
      subtitle:
        'Choose the plan that works for you. One-time activation, no subscriptions.',
    },
  },

  // ─────────────────────────────────────────────
  // PORTUGUESE
  // ─────────────────────────────────────────────
  pt: {
    lp: {
      menuFeatures: 'Recursos',
      getStarted: 'Começar',
      heroEyebrow: 'Plataforma de mídia para Smart TV',
      heroH1a: 'Controle inteligente',
      heroH1b: 'da sua TV para uma',
      heroH1c: 'nova geração.',
      heroLead: 'Organize seu catálogo, controle a TV pelo celular e aproveite um entretenimento mais seguro e inteligente.',
      heroTry: 'Teste grátis por 7 dias →',
      heroPlans: 'Ver planos',
      trust1: 'Ativação rápida',
      trust2: 'Funciona em Smart TVs',
      trust3: 'Seguro e privado',
      compatTitle: 'Compatível com as principais plataformas de Smart TV',
      moreName: 'e mais',
      moreSub: 'Plataformas',
      featKicker: 'Recursos poderosos',
      featTitleA: 'Tudo o que você precisa,',
      featTitleB: 'feito para uma TV melhor.',
      learn: 'Saiba mais →',
      f1t: 'Controle pelo celular',
      f1d: 'Use o smartphone como controle remoto. Rápido, intuitivo e sempre à mão.',
      f2t: 'Mais seguro para a família',
      f2d: 'Proteção por PIN, filtros de conteúdo e controle parental para manter todos seguros.',
      f3t: 'Reprodução poderosa',
      f3d: 'Desempenho fluido, suporte a múltiplos formatos e qualidade de streaming premium.',
      f4t: 'Catálogo organizado',
      f4d: 'Importe, ordene e gerencie seu conteúdo com categorias bonitas.',
      stat1: 'Plataformas compatíveis',
      stat2: 'Idiomas suportados',
      stat3: 'Ativação rápida média',
      stat4: 'Suporte premium',
      setupKicker: 'Configuração fácil',
      setupTitle: 'Comece em 3 passos simples',
      step1t: 'Ative sua TV',
      step1d: 'Escaneie o QR code ou digite o código de ativação na sua TV para conectar em segundos.',
      step2t: 'Adicione seu conteúdo',
      step2d: 'Importe suas playlists ou fontes de mídia e organize do seu jeito.',
      step3t: 'Assista e controle',
      step3d: 'Aproveite seu conteúdo e controle tudo pelo celular, a qualquer hora e lugar.',
      priceKicker: 'Planos',
      priceTitle: 'Escolha o plano ideal para você',
      priceSub: 'Uma plataforma. Todo o seu entretenimento. Do seu jeito.',
      annualName: 'Anual',
      annualDesc: 'Acesso por 12 meses',
      annualUnit: '/ano',
      annualF1: 'Player completo sem restrições',
      annualF2: 'Suporte a playlists',
      annualF3: 'Controle parental',
      annualF4: 'Segurança por PIN',
      annualF5: 'Catálogo organizado',
      annualCta: 'Assinar plano anual',
      bestValue: 'Melhor custo',
      lifeName: 'Vitalício',
      lifeDesc: 'Pagamento único',
      lifeUnit: 'para sempre',
      lifeF1: 'Tudo do plano Anual',
      lifeF2: 'Acesso vitalício',
      lifeF3: 'Atualizações futuras incluídas',
      lifeF4: 'Sem renovação',
      lifeF5: 'Suporte prioritário',
      lifeCta: 'Obter acesso vitalício',
      ben1: '7 dias grátis',
      ben2: 'Pagamento seguro',
      ben3: 'Cancele quando quiser',
      ctaTitle: 'O futuro da TV está nas suas mãos.',
      ctaSub: 'Junte-se a milhares de usuários que aproveitam um entretenimento mais inteligente, seguro e poderoso com a MoovaTV.',
      ctaBtn: 'Teste a MoovaTV grátis por 7 dias →',
      pnpKicker: 'Player, não provedor',
      pnpTitleA: 'Vendemos o player.',
      pnpTitleB: 'Nunca o conteúdo.',
      pnpLead: 'A MoovaTV não vem com canais, playlists nem assinaturas de conteúdo. Você traz suas próprias fontes; nós fazemos elas parecerem premium na sua TV.',
      pnpYesT: 'O que você recebe',
      pnpYes1: 'Um player premium para suas próprias playlists e fontes de mídia',
      pnpYes2: 'EPG, favoritos, controle parental e sincronização multi-dispositivo',
      pnpYes3: 'Celular como controle em Tizen, webOS, Roku, Google TV e mais',
      pnpNoT: 'O que nunca vendemos',
      pnpNo1: 'Nenhum canal de TV ou transmissão ao vivo',
      pnpNo2: 'Nenhuma playlist, pacote ou assinatura de conteúdo',
      pnpNo3: 'Nenhuma hospedagem de conteúdo: suas fontes continuam suas',
      footTagline: 'Controle inteligente da TV para uma experiência de nova geração.',
      footProduct: 'Produto',
      footFeatures: 'Recursos',
      footActivation: 'Ativação',
      footPlaylist: 'Playlist',
      footDevices: 'Dispositivos',
      footCompany: 'Empresa',
      footContact: 'Contato',
      footAbout: 'Sobre nós',
      footReseller: 'Revenda',
      footBecome: 'Seja um revendedor',
      footLogin: 'Login do revendedor',
      footLegal: 'Legal',
      footPrivacy: 'Política de Privacidade',
      footTerms: 'Termos de Uso',
      footCopy: '© 2026 MoovaTV. Todos os direitos reservados. · Seguro · Privado · Confiável',
    },
    nav: {
      home: 'Início',
      activation: 'Ativação',
      playlist: 'Playlist',
      reseller: 'Revenda',
      pricing: 'Preços',
      skipToContent: 'Pular para o conteúdo',
      primary: 'Principal',
      language: 'Idioma',
      openMenu: 'Abrir menu',
      closeMenu: 'Fechar menu',
    },
    footer: {
      tagline: 'Mais controle, organização e segurança para sua experiência na TV.',
      product: 'Produto',
      partners: 'Parceiros',
      becomeReseller: 'Seja um Revendedor',
      legal: 'Legal',
      privacy: 'Política de Privacidade',
      terms: 'Termos de Uso',
      copyright: 'Todos os direitos reservados.',
      disclaimer:
        'MoovaTV é exclusivamente um player de mídia. Não comercializamos, distribuímos ou fornecemos listas, canais ou conteúdo de qualquer tipo.',
    },
    home: {
      metaTitle: 'MoovaTV — Player de mídia para Smart TVs com controle pelo celular',
      metaDesc: 'Organize seu catálogo, controle a TV pelo celular e assista com mais segurança em Smart TVs compatíveis. Teste grátis por 7 dias.',
      heroTitle1: 'Controle sua experiência na TV com um player',
      heroTitle2: 'mais inteligente.',
      heroSub:
        'Organize seu catálogo, controle a TV pelo celular e assista com mais segurança em Smart TVs compatíveis.',
      heroCta: 'Testar grátis por 7 dias',
      heroPlans: 'Ver planos',
      heroMicro: 'Ativação rápida. Sem cartão no teste grátis.',
      marqueeLabel: 'Compatível com as principais plataformas e marcas de Smart TV.',
      featuresLabel: 'Recursos',
      featuresTitle: 'Tudo o que você precisa para assistir com mais controle.',
      featuresSub: 'Mais conforto no uso, mais organização no catálogo e mais segurança no acesso.',
      feat1Title: 'Controle tudo pelo celular',
      feat1Desc:
        'Navegue pelo catálogo, envie para a TV e controle a reprodução em tempo real pela segunda tela.',
      feat2Title: 'Mais segurança para sua casa',
      feat2Desc:
        'Use PIN, modo infantil e filtros de navegação para ter mais controle sobre o acesso ao conteúdo.',
      feat3Title: 'Reprodução mais completa',
      feat3Desc:
        'Tenha legendas, múltiplos áudios e qualidade adaptativa para uma experiência mais estável e confortável.',
      feat4Title: 'Catálogo mais organizado',
      feat4Desc:
        'Visual mais rico com capas, sinopses e informações que ajudam você a encontrar o que quer assistir mais rápido.',
      syncLabel: 'Segunda tela',
      syncTitle1: 'Escolha no celular.',
      syncTitle2: 'Assista na TV.',
      syncSub: 'Navegue com mais agilidade, envie o conteúdo para a TV e controle tudo sem depender de um controle remoto confuso.',
      syncMicro: 'Sincronização em tempo real',
      syncStep0: 'Navegando pelo catálogo',
      syncStep1: 'Selecionando filme',
      syncStep2: 'Vendo detalhes',
      syncStep3: 'Enviando para a TV',
      syncStep4: 'Reproduzindo na TV',
      syncStep5: 'Controle pelo celular',
      syncStep6: 'Pausado pelo celular',
      remoteNowPlaying: 'Tocando agora',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'Pausado',
      syncWatchOnTv: 'Assistir na TV',
      syncSentToTv: 'Enviado para TV',
      syncTvWaiting: 'Aguardando',
      numbersSub: 'Compatibilidade ampla e configuração rápida para começar sem complicação.',
      stepsTitle: 'Comece em poucos minutos',
      stepsSub: 'Ative sua TV, adicione seu conteúdo e controle tudo pelo celular.',
      step: 'Passo',
      step1Title: 'Ative sua TV',
      step1Desc:
        'Escaneie o QR code na tela ou digite o código de ativação. O processo leva menos de 30 segundos.',
      step2Title: 'Adicione seu conteúdo',
      step2Desc:
        'Importe sua playlist e deixe tudo mais organizado por categorias, qualidade e gênero.',
      step3Title: 'Assista e controle',
      step3Desc:
        'Navegue pelo catálogo no celular, envie para a TV e gerencie a reprodução em tempo real.',
      pricingTitle: 'Escolha o plano ideal para usar o player',
      pricingSub:
        'Você paga pelo acesso ao player e aos recursos da plataforma. A MoovaTV não comercializa listas, canais ou conteúdo.',
      planFreeName: 'Teste grátis',
      planFreeInfo: 'Experimente a plataforma por 7 dias',
      planFreePrice: 'Grátis',
      planFreeFeat1: 'Acesso completo durante o período de teste',
      planFreeFeat2: 'Todas as funcionalidades liberadas',
      planFreeFeat3: 'Sem cartão de crédito',
      planFreeCta: 'Começar grátis',
      planAnnualName: 'Anual',
      planAnnualInfo: 'Acesso por 12 meses',
      planAnnualDetail: '/ ano',
      planAnnualFeat1: 'Player completo sem restrições',
      planAnnualFeat2: 'Controle pelo celular',
      planAnnualFeat3: 'Segurança com PIN',
      planAnnualFeat4: 'Catálogo organizado e enriquecido',
      planAnnualFeat5: 'Interface em 7 idiomas',
      planAnnualFeat6: 'Suporte prioritário',
      planAnnualCta: 'Assinar anual',
      planLifetimeName: 'Vitalício',
      planGoldName: 'Gold Vitalício',
      planGoldInfo: 'Pagamento único',
      planGoldDetail: 'para sempre',
      planGoldBadge: 'Melhor custo-benefício',
      planGoldSavings: 'Melhor custo-benefício',
      planGoldFeat1: 'Tudo do plano anual',
      planGoldFeat2: 'Acesso vitalício ao player',
      planGoldFeat3: 'Sem renovação',
      planGoldFeat4: 'Atualizações futuras incluídas',
      planGoldFeat5: 'Suporte prioritário',
      planGoldCta: 'Ativar agora',
      ctaTitle1: 'Uma experiência melhor na sua TV',
      ctaTitle2: 'começa aqui.',
      ctaCta: 'Testar grátis por 7 dias',
      ctaMicro: 'Ative em minutos e experimente todos os recursos.',
      platforms: 'PLATAFORMAS COMPATÍVEIS',
      epgComplete: 'GUIA EPG COMPLETO',
      languages: 'IDIOMAS DISPONÍVEIS',
      activation: 'ATIVAÇÃO RÁPIDA',
    },
    activation: {
      title: 'Ative seu dispositivo',
      subtitle: 'Assine para liberar o player Moova na sua Smart TV',
      step1: 'Digite o endereço MAC exibido na sua TV',
      step2: 'Escolha o plano ideal para você',
      step3: 'Assine — sua TV ativa na hora',
      planLifetime: 'Vitalício',
      planAnnual: 'Anual',
      planMonthly: 'Mensal',
      choosePlan: 'Escolha seu plano',
      choosePlanSub: 'Selecione a opção que melhor combina com você',
      macLabel: 'Endereço MAC',
      checking: 'Verificando dispositivo...',
      found: 'Dispositivo encontrado — escolha um plano para ativar',
      alreadyPaid: 'Este dispositivo já está ativado. Nenhum pagamento necessário.',
      notFound:
        'Não encontrado. Instale e abra o app na sua TV primeiro.',
      smartTv: 'Smart TV',
      securePayment: 'Pagamento seguro',
      instantActivation: 'Ativação instantânea',
      acceptTerms: 'Eu aceito os',
      termsLink: 'termos de uso e política de privacidade',
      processing: 'Processando...',
      activateBtn: 'Ativar',
      activateDevice: 'Ativar Dispositivo',
      disclaimer:
        'O app não inclui playlists ou canais. Configure sua própria playlist após a ativação.',
      popular: 'Popular',
      oneTime: 'Pagamento único',
      perYear: 'Por ano',
      perMonth: 'Por mês',
      errorPayment:
        'Não foi possível gerar o link de pagamento. Tente novamente.',
      errorGeneric: 'Erro ao processar pagamento. Tente novamente.',
      macFound: 'Encontrado',
      descLifetime: 'Pague uma vez, use para sempre',
      descAnnual: 'Renovação anual',
      descMonthly: 'Renovação mensal',
      alreadyPaidUntil: 'Este dispositivo já tem um plano ativo até {date}. Nenhum pagamento necessário.',
      alreadyLifetime: 'Este dispositivo já tem o plano Vitalício. Nenhum pagamento necessário.',
    },
    playlist: {
      title: 'Enviar Playlist',
      subtitle:
        'Carregue sua própria playlist no app. Suporta M3U, M3U8 e outras fontes.',
      step1: 'Digite o endereço MAC do dispositivo da sua TV',
      step2: 'Escolha o método de envio e forneça a playlist',
      step3: 'Envie — a playlist sincroniza com sua TV instantaneamente',
      multipleFormats: 'Múltiplos formatos',
      multipleFormatsDesc:
        'Envie via URL, arquivo, credenciais do servidor ou link M3U8 direto.',
      epgSupport: 'Suporte a EPG',
      epgSupportDesc:
        'Adicione uma URL de Guia Eletrônico de Programação para grades de canais.',
      pinProtection: 'Proteção por PIN',
      pinProtectionDesc:
        'Proteja opcionalmente sua playlist com um PIN de 4 dígitos.',
      macLabel: 'Endereço MAC',
      checking: 'Verificando dispositivo...',
      found: 'Dispositivo encontrado — você pode enviar sua playlist',
      notFound:
        'Não encontrado. Instale e abra o app na sua TV primeiro.',
      playlistName: 'Nome da Playlist',
      playlistPlaceholder: 'Minha Playlist',
      uploadMethod: 'Método de envio',
      urlTab: 'URL',
      fileTab: 'Arquivo',
      hostTab: 'Host',
      m3u8Tab: 'M3U8',
      m3uUrl: 'URL M3U',
      m3u8Url: 'URL M3U8',
      m3u8Note: 'Para listas M3U8 de outras fontes',
      m3uFile: 'Arquivo M3U/M3U8',
      hostLabel: 'Host',
      usernameLabel: 'Usuário',
      passwordLabel: 'Senha',
      options: 'Opções',
      epgUrl: 'URL do EPG (opcional)',
      protectPin: 'Proteger playlist com PIN',
      pinPlaceholder: 'PIN de 4 dígitos',
      setDefault: 'Definir como playlist padrão',
      uploading: 'Enviando...',
      uploadBtn: 'Enviar Playlist',
      uploadSuccess: 'Playlist enviada com sucesso!',
      uploadError:
        'Erro ao enviar playlist. Verifique o MAC e tente novamente.',
      fileError: 'Apenas arquivos .m3u e .m3u8 são suportados',
    },
    reseller: {
      title: 'Seja um Revendedor',
      subtitle:
        'Participe do nosso programa de parceiros e comece a ganhar com ativações MoovaTV.',
      step1: 'Crie sua conta de revendedor',
      step2: 'Acesse o painel e gere códigos de ativação',
      step3: 'Venda para seus clientes e ganhe comissões',
      manageClients: 'Gerencie seus clientes',
      manageClientsDesc:
        'Painel completo para acompanhar ativações, clientes e ganhos.',
      competitivePricing: 'Preços competitivos',
      competitivePricingDesc:
        'Compre créditos em volume com preços com desconto.',
      realTimeAnalytics: 'Análises em tempo real',
      realTimeAnalyticsDesc:
        'Acompanhe sua receita, dispositivos ativos e crescimento.',
      createAccount: 'Crie sua conta',
      fieldsRequired: 'Campos marcados com * são obrigatórios',
      nameLabel: 'Nome',
      namePlaceholder: 'Primeiro nome',
      surnameLabel: 'Sobrenome',
      surnamePlaceholder: 'Último nome',
      emailLabel: 'Email',
      emailPlaceholder: 'voce@email.com',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Mínimo 6 caracteres',
      confirmLabel: 'Confirmar',
      confirmPlaceholder: 'Repita a senha',
      passwordMismatch: 'As senhas não coincidem',
      optional: 'Opcional',
      companyLabel: 'Empresa',
      companyPlaceholder: 'Nome da empresa',
      phoneLabel: 'Telefone',
      countryLabel: 'País',
      countryPlaceholder: 'Brasil',
      countrySelect: 'Selecione seu país',
      countrySearch: 'Buscar país...',
      countryNone: 'Nenhum país encontrado',
      phoneInvalid: 'Telefone inválido para o país selecionado',
      dialCode: 'Código do país',
      partnerType: 'Tipo de parceiro',
      resellerOption: 'Revendedor',
      resellerDesc: 'Compre e venda ativações',
      referralOption: 'Indicação',
      referralDesc: 'Ganhe por indicação',
      creatingAccount: 'Criando conta...',
      createBtn: 'Criar Conta de Revendedor',
      registerSuccess: 'Cadastro realizado com sucesso! Redirecionando...',
      registerError: 'Erro ao realizar cadastro. Tente novamente.',
    },
    pricing: {
      title: 'Preços simples e transparentes',
      subtitle:
        'Escolha o plano ideal para você. Ativação única, sem assinaturas.',
    },
  },

  // ─────────────────────────────────────────────
  // GERMAN
  // ─────────────────────────────────────────────
  de: {
    nav: {
      home: 'Startseite',
      activation: 'Aktivierung',
      playlist: 'Playlist',
      reseller: 'Wiederverkäufer',
      pricing: 'Preise',
      skipToContent: 'Zum Inhalt springen',
      primary: 'Hauptmenü',
      language: 'Sprache',
      openMenu: 'Menü öffnen',
      closeMenu: 'Menü schließen',
    },
    footer: {
      tagline: 'Mehr Kontrolle, Organisation und Sicherheit für dein TV-Erlebnis.',
      product: 'Produkt',
      partners: 'Partner',
      becomeReseller: 'Wiederverkäufer werden',
      legal: 'Rechtliches',
      privacy: 'Datenschutzrichtlinie',
      terms: 'Nutzungsbedingungen',
      copyright: 'Alle Rechte vorbehalten.',
      disclaimer:
        'MoovaTV ist ausschließlich ein Media-Player. Wir verkaufen, vertreiben oder stellen keine Playlisten, Kanäle oder Inhalte jeglicher Art bereit.',
    },
    home: {
      metaTitle: 'MoovaTV — Media-Player für Smart TVs mit Handy-Steuerung',
      metaDesc: 'Organisiere deinen Katalog, steuere den TV per Handy und schaue sicherer auf kompatiblen Smart TVs. 7 Tage kostenlos testen.',
      heroTitle1: 'Übernimm die Kontrolle über dein TV-Erlebnis mit einem smarteren',
      heroTitle2: 'Media-Player.',
      heroSub:
        'Organisiere deinen Katalog, steuere den TV per Handy und schaue sicherer auf kompatiblen Smart TVs.',
      heroCta: '7 Tage kostenlos testen',
      heroPlans: 'Pläne ansehen',
      heroMicro: 'Schnelle Aktivierung. Keine Kreditkarte für den Test.',
      marqueeLabel: 'Kompatibel mit den führenden Smart-TV-Plattformen und -Marken.',
      featuresLabel: 'Funktionen',
      featuresTitle: 'Alles, was du brauchst, um mit mehr Kontrolle zu schauen.',
      featuresSub: 'Mehr Komfort, bessere Organisation und stärkere Sicherheit für dein Seherlebnis.',
      feat1Title: 'Steuere alles per Handy',
      feat1Desc:
        'Durchsuche den Katalog, sende an den TV und steuere die Wiedergabe in Echtzeit über den zweiten Bildschirm.',
      feat2Title: 'Mehr Sicherheit für dein Zuhause',
      feat2Desc:
        'Nutze PIN, Kindermodus und Filter für mehr Kontrolle über den Zugang zu Inhalten.',
      feat3Title: 'Vollständigere Wiedergabe',
      feat3Desc:
        'Untertitel, mehrere Audiospuren und adaptive Qualität für ein stabileres und komfortableres Erlebnis.',
      feat4Title: 'Besser organisierter Katalog',
      feat4Desc:
        'Reichere Darstellung mit Covern, Beschreibungen und Infos, damit du schneller findest, was du sehen willst.',
      syncLabel: 'Zweiter Bildschirm',
      syncTitle1: 'Wähle auf dem Handy.',
      syncTitle2: 'Schaue auf dem TV.',
      syncSub: 'Navigiere schneller, sende Inhalte an den TV und steuere alles ohne eine verwirrende Fernbedienung.',
      syncMicro: 'Echtzeit-Synchronisierung',
      syncStep0: 'Katalog durchstöbern',
      syncStep1: 'Film auswählen',
      syncStep2: 'Details ansehen',
      syncStep3: 'An TV senden',
      syncStep4: 'Wiedergabe am TV',
      syncStep5: 'Handy als Fernbedienung',
      syncStep6: 'Vom Handy pausiert',
      remoteNowPlaying: 'Läuft jetzt',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'Pausiert',
      syncWatchOnTv: 'Auf dem TV ansehen',
      syncSentToTv: 'An TV gesendet',
      syncTvWaiting: 'Warten',
      numbersSub: 'Breite Kompatibilität und schnelle Einrichtung für einen unkomplizierten Start.',
      stepsTitle: 'In wenigen Minuten starten',
      stepsSub: 'Aktiviere deinen TV, füge Inhalte hinzu und steuere alles per Handy.',
      step: 'Schritt',
      step1Title: 'Aktiviere deinen TV',
      step1Desc:
        'Scanne den QR-Code auf dem Bildschirm oder gib den Aktivierungscode ein. Der Vorgang dauert weniger als 30 Sekunden.',
      step2Title: 'Füge deine Inhalte hinzu',
      step2Desc:
        'Importiere deine Playlist und halte alles nach Kategorie, Qualität und Genre organisiert.',
      step3Title: 'Schauen und steuern',
      step3Desc:
        'Durchsuche den Katalog auf dem Handy, sende an den TV und verwalte die Wiedergabe in Echtzeit.',
      pricingTitle: 'Wähle den passenden Plan für den Player',
      pricingSub:
        'Du zahlst für den Zugang zum Player und den Plattform-Funktionen. MoovaTV verkauft keine Playlisten, Kanäle oder Inhalte.',
      planFreeName: 'Kostenloser Test',
      planFreeInfo: 'Teste die Plattform 7 Tage lang',
      planFreePrice: 'Kostenlos',
      planFreeFeat1: 'Voller Zugang während des Testzeitraums',
      planFreeFeat2: 'Alle Funktionen freigeschaltet',
      planFreeFeat3: 'Keine Kreditkarte nötig',
      planFreeCta: 'Kostenlos starten',
      planAnnualName: 'Jährlich',
      planAnnualInfo: 'Zugang für 12 Monate',
      planAnnualDetail: '/ Jahr',
      planAnnualFeat1: 'Vollständiger Player ohne Einschränkungen',
      planAnnualFeat2: 'Steuerung per Handy',
      planAnnualFeat3: 'PIN-Sicherheit',
      planAnnualFeat4: 'Organisierter und erweiterter Katalog',
      planAnnualFeat5: 'Oberfläche in 7 Sprachen',
      planAnnualFeat6: 'Prioritäts-Support',
      planAnnualCta: 'Jährlich abonnieren',
      planLifetimeName: 'Lifetime',
      planGoldName: 'Gold Lifetime',
      planGoldInfo: 'Einmalzahlung',
      planGoldDetail: 'für immer',
      planGoldBadge: 'Bestes Preis-Leistungs-Verhältnis',
      planGoldSavings: 'Bestes Preis-Leistungs-Verhältnis',
      planGoldFeat1: 'Alles aus dem Jahresplan',
      planGoldFeat2: 'Lebenslanger Player-Zugang',
      planGoldFeat3: 'Keine Verlängerung',
      planGoldFeat4: 'Zukünftige Updates inklusive',
      planGoldFeat5: 'Prioritäts-Support',
      planGoldCta: 'Jetzt aktivieren',
      ctaTitle1: 'Ein besseres TV-Erlebnis',
      ctaTitle2: 'beginnt hier.',
      ctaCta: '7 Tage kostenlos testen',
      ctaMicro: 'In Minuten aktivieren und alle Funktionen ausprobieren.',
      platforms: 'KOMPATIBLE PLATTFORMEN',
      epgComplete: 'VOLLSTÄNDIGER EPG-GUIDE',
      languages: 'VERFÜGBARE SPRACHEN',
      activation: 'SCHNELLE AKTIVIERUNG',
    },
    activation: {
      title: 'Gerät aktivieren',
      subtitle:
        'Abonniere, um den Moova-Player auf deinem Smart TV freizuschalten',
      step1: 'Gib die auf deinem TV angezeigte MAC-Adresse ein',
      step2: 'Wähle einen passenden Plan',
      step3: 'Abonniere — dein TV wird sofort aktiviert',
      planLifetime: 'Lebenslang',
      planAnnual: 'Jährlich',
      planMonthly: 'Monatlich',
      choosePlan: 'Wähle deinen Plan',
      choosePlanSub: 'Wähle die Option, die am besten zu dir passt',
      macLabel: 'MAC-Adresse',
      checking: 'Gerät wird überprüft...',
      found: 'Gerät gefunden — wähle einen Plan zur Aktivierung',
      alreadyPaid: 'Dieses Gerät ist bereits aktiviert. Keine Zahlung erforderlich.',
      notFound:
        'Nicht gefunden. Installiere und öffne zuerst die App auf deinem TV.',
      smartTv: 'Smart TV',
      securePayment: 'Sichere Zahlung',
      instantActivation: 'Sofortige Aktivierung',
      acceptTerms: 'Ich akzeptiere die',
      termsLink: 'Nutzungsbedingungen und Datenschutzrichtlinie',
      processing: 'Verarbeitung...',
      activateBtn: 'Aktivieren',
      activateDevice: 'Gerät aktivieren',
      disclaimer:
        'Die App enthält keine Playlisten oder Kanäle. Richte deine eigene Playlist nach der Aktivierung ein.',
      popular: 'Beliebt',
      oneTime: 'Einmalzahlung',
      perYear: 'Pro Jahr',
      perMonth: 'Pro Monat',
      errorPayment:
        'Zahlungslink konnte nicht erstellt werden. Bitte versuche es erneut.',
      errorGeneric:
        'Fehler bei der Zahlungsverarbeitung. Bitte versuche es erneut.',
      macFound: 'Gefunden',
      descLifetime: 'Einmal zahlen, für immer nutzen',
      descAnnual: 'Jährliche Verlängerung',
      descMonthly: 'Monatliche Verlängerung',
      alreadyPaidUntil: 'Dieses Gerät hat bereits einen aktiven Plan bis {date}. Keine Zahlung erforderlich.',
      alreadyLifetime: 'Dieses Gerät hat bereits den Lebenslang-Plan. Keine Zahlung erforderlich.',
    },
    playlist: {
      title: 'Playlist hochladen',
      subtitle:
        'Lade deine eigene Playlist in die App. Unterstützt M3U, M3U8 und andere Quellen.',
      step1: 'Gib die MAC-Adresse deines TV-Geräts ein',
      step2: 'Wähle eine Upload-Methode und stelle die Playlist bereit',
      step3: 'Absenden — die Playlist wird sofort mit deinem TV synchronisiert',
      multipleFormats: 'Mehrere Formate',
      multipleFormatsDesc:
        'Upload per URL, Datei, Server-Zugangsdaten oder direktem M3U8-Link.',
      epgSupport: 'EPG-Unterstützung',
      epgSupportDesc:
        'Füge eine EPG-URL für Programmübersichten hinzu.',
      pinProtection: 'PIN-Schutz',
      pinProtectionDesc:
        'Schütze deine Playlist optional mit einer 4-stelligen PIN.',
      macLabel: 'MAC-Adresse',
      checking: 'Gerät wird überprüft...',
      found: 'Gerät gefunden — du kannst deine Playlist hochladen',
      notFound:
        'Nicht gefunden. Installiere und öffne zuerst die App auf deinem TV.',
      playlistName: 'Playlist-Name',
      playlistPlaceholder: 'Meine Playlist',
      uploadMethod: 'Upload-Methode',
      urlTab: 'URL',
      fileTab: 'Datei',
      hostTab: 'Host',
      m3u8Tab: 'M3U8',
      m3uUrl: 'M3U-URL',
      m3u8Url: 'M3U8-URL',
      m3u8Note: 'Für M3U8-Listen aus anderen Quellen',
      m3uFile: 'M3U/M3U8-Datei',
      hostLabel: 'Host',
      usernameLabel: 'Benutzername',
      passwordLabel: 'Passwort',
      options: 'Optionen',
      epgUrl: 'EPG-URL (optional)',
      protectPin: 'Playlist mit PIN schützen',
      pinPlaceholder: '4-stellige PIN',
      setDefault: 'Als Standard-Playlist festlegen',
      uploading: 'Hochladen...',
      uploadBtn: 'Playlist hochladen',
      uploadSuccess: 'Playlist erfolgreich hochgeladen!',
      uploadError:
        'Fehler beim Hochladen der Playlist. Überprüfe die MAC und versuche es erneut.',
      fileError: 'Nur .m3u- und .m3u8-Dateien werden unterstützt',
    },
    reseller: {
      title: 'Wiederverkäufer werden',
      subtitle:
        'Tritt unserem Partnerprogramm bei und verdiene mit MoovaTV-Aktivierungen.',
      step1: 'Erstelle dein Wiederverkäufer-Konto',
      step2: 'Greife auf das Dashboard zu und erstelle Aktivierungscodes',
      step3: 'Verkaufe an deine Kunden und verdiene Provisionen',
      manageClients: 'Verwalte deine Kunden',
      manageClientsDesc:
        'Vollständiges Dashboard zum Verfolgen von Aktivierungen, Kunden und Einnahmen.',
      competitivePricing: 'Wettbewerbsfähige Preise',
      competitivePricingDesc:
        'Kaufe Credits in großen Mengen zu ermäßigten Preisen.',
      realTimeAnalytics: 'Echtzeit-Analysen',
      realTimeAnalyticsDesc:
        'Verfolge deinen Umsatz, aktive Geräte und Wachstum.',
      createAccount: 'Konto erstellen',
      fieldsRequired: 'Mit * markierte Felder sind Pflichtfelder',
      nameLabel: 'Name',
      namePlaceholder: 'Vorname',
      surnameLabel: 'Nachname',
      surnamePlaceholder: 'Nachname',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'du@email.com',
      passwordLabel: 'Passwort',
      passwordPlaceholder: 'Mindestens 6 Zeichen',
      confirmLabel: 'Bestätigen',
      confirmPlaceholder: 'Passwort wiederholen',
      passwordMismatch: 'Passwörter stimmen nicht überein',
      optional: 'Optional',
      companyLabel: 'Unternehmen',
      companyPlaceholder: 'Firmenname',
      phoneLabel: 'Telefon',
      countryLabel: 'Land',
      countryPlaceholder: 'Deutschland',
      countrySelect: 'Land auswählen',
      countrySearch: 'Land suchen...',
      countryNone: 'Kein Land gefunden',
      phoneInvalid: 'Ungültige Telefonnummer für das gewählte Land',
      dialCode: 'Ländervorwahl',
      partnerType: 'Partnertyp',
      resellerOption: 'Wiederverkäufer',
      resellerDesc: 'Aktivierungen kaufen und verkaufen',
      referralOption: 'Empfehlung',
      referralDesc: 'Pro Empfehlung verdienen',
      creatingAccount: 'Konto wird erstellt...',
      createBtn: 'Wiederverkäufer-Konto erstellen',
      registerSuccess: 'Registrierung erfolgreich! Weiterleitung...',
      registerError:
        'Registrierung fehlgeschlagen. Bitte versuche es erneut.',
    },
    pricing: {
      title: 'Einfache, transparente Preise',
      subtitle:
        'Wähle den Plan, der zu dir passt. Einmalige Aktivierung, kein Abo.',
    },
  },

  // ─────────────────────────────────────────────
  // FRENCH
  // ─────────────────────────────────────────────
  fr: {
    nav: {
      home: 'Accueil',
      activation: 'Activation',
      playlist: 'Playlist',
      reseller: 'Revendeur',
      pricing: 'Tarifs',
      skipToContent: 'Aller au contenu',
      primary: 'Principal',
      language: 'Langue',
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
    },
    footer: {
      tagline: 'Plus de contrôle, d\'organisation et de sécurité pour votre expérience TV.',
      product: 'Produit',
      partners: 'Partenaires',
      becomeReseller: 'Devenir Revendeur',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      terms: "Conditions d'utilisation",
      copyright: 'Tous droits réservés.',
      disclaimer:
        "MoovaTV est exclusivement un lecteur multimédia. Nous ne vendons, distribuons ni fournissons de playlists, chaînes ou contenu d'aucune sorte.",
    },
    home: {
      metaTitle: 'MoovaTV — Lecteur multimédia pour Smart TVs avec contrôle par téléphone',
      metaDesc: 'Organisez votre catalogue, contrôlez la TV depuis votre téléphone et regardez en toute sécurité sur les Smart TVs compatibles. Essai gratuit de 7 jours.',
      heroTitle1: 'Prenez le contrôle de votre TV avec un lecteur',
      heroTitle2: 'plus intelligent.',
      heroSub:
        'Organisez votre catalogue, contrôlez la TV depuis votre téléphone et regardez en toute sécurité sur les Smart TVs compatibles.',
      heroCta: 'Essayer gratuitement 7 jours',
      heroPlans: 'Voir les offres',
      heroMicro: 'Activation rapide. Sans carte bancaire pour l\'essai gratuit.',
      marqueeLabel: 'Compatible avec les principales plateformes et marques de Smart TV.',
      featuresLabel: 'Fonctionnalités',
      featuresTitle: 'Tout ce dont vous avez besoin pour regarder avec plus de contrôle.',
      featuresSub: 'Plus de confort, une meilleure organisation et une sécurité renforcée pour votre expérience.',
      feat1Title: 'Contrôlez tout depuis votre téléphone',
      feat1Desc:
        'Parcourez le catalogue, envoyez à la TV et contrôlez la lecture en temps réel via le second écran.',
      feat2Title: 'Plus de sécurité pour votre foyer',
      feat2Desc:
        'Utilisez PIN, mode enfant et filtres de navigation pour plus de contrôle sur l\'accès au contenu.',
      feat3Title: 'Lecture plus complète',
      feat3Desc:
        'Sous-titres, pistes audio multiples et qualité adaptative pour une expérience plus stable et confortable.',
      feat4Title: 'Catalogue mieux organisé',
      feat4Desc:
        'Un visuel enrichi avec affiches, synopsis et informations pour trouver plus vite ce que vous voulez regarder.',
      syncLabel: 'Second Écran',
      syncTitle1: 'Choisissez sur le téléphone.',
      syncTitle2: 'Regardez sur la TV.',
      syncSub: 'Naviguez plus rapidement, envoyez le contenu à la TV et contrôlez tout sans dépendre d\'une télécommande compliquée.',
      syncMicro: 'Synchronisation en temps réel',
      syncStep0: 'Parcourir le catalogue',
      syncStep1: 'Choisir un film',
      syncStep2: 'Voir les détails',
      syncStep3: 'Envoi vers la TV',
      syncStep4: 'Lecture sur la TV',
      syncStep5: 'Téléphone comme télécommande',
      syncStep6: 'Mis en pause depuis le téléphone',
      remoteNowPlaying: 'En cours',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'En pause',
      syncWatchOnTv: 'Regarder sur la TV',
      syncSentToTv: 'Envoyé à la TV',
      syncTvWaiting: 'En attente',
      numbersSub: 'Large compatibilité et configuration rapide pour démarrer sans complication.',
      stepsTitle: 'Démarrez en quelques minutes',
      stepsSub: 'Activez votre TV, ajoutez votre contenu et contrôlez tout depuis votre téléphone.',
      step: 'Étape',
      step1Title: 'Activez votre TV',
      step1Desc:
        "Scannez le QR code à l'écran ou saisissez le code d'activation. Le processus prend moins de 30 secondes.",
      step2Title: 'Ajoutez votre contenu',
      step2Desc:
        'Importez votre playlist et gardez tout organisé par catégorie, qualité et genre.',
      step3Title: 'Regardez et contrôlez',
      step3Desc:
        'Parcourez le catalogue sur votre téléphone, envoyez à la TV et gérez la lecture en temps réel.',
      pricingTitle: 'Choisissez le plan idéal pour le lecteur',
      pricingSub:
        "Vous payez pour l'accès au lecteur et aux fonctionnalités de la plateforme. MoovaTV ne vend pas de playlists, chaînes ou contenu.",
      planFreeName: 'Essai gratuit',
      planFreeInfo: 'Essayez la plateforme pendant 7 jours',
      planFreePrice: 'Gratuit',
      planFreeFeat1: 'Accès complet pendant la période d\'essai',
      planFreeFeat2: 'Toutes les fonctionnalités débloquées',
      planFreeFeat3: 'Sans carte bancaire',
      planFreeCta: 'Commencer gratuitement',
      planAnnualName: 'Annuel',
      planAnnualInfo: 'Accès pour 12 mois',
      planAnnualDetail: '/ an',
      planAnnualFeat1: 'Lecteur complet sans restrictions',
      planAnnualFeat2: 'Contrôle par téléphone',
      planAnnualFeat3: 'Sécurité par PIN',
      planAnnualFeat4: 'Catalogue organisé et enrichi',
      planAnnualFeat5: 'Interface en 7 langues',
      planAnnualFeat6: 'Support prioritaire',
      planAnnualCta: "S'abonner à l'année",
      planLifetimeName: 'Lifetime',
      planGoldName: 'Gold Lifetime',
      planGoldInfo: 'Paiement unique',
      planGoldDetail: 'pour toujours',
      planGoldBadge: 'Meilleur rapport qualité-prix',
      planGoldSavings: 'Meilleur rapport qualité-prix',
      planGoldFeat1: "Tout du plan Annuel",
      planGoldFeat2: 'Accès à vie au lecteur',
      planGoldFeat3: 'Sans renouvellement',
      planGoldFeat4: 'Mises à jour futures incluses',
      planGoldFeat5: 'Support prioritaire',
      planGoldCta: 'Activer maintenant',
      ctaTitle1: 'Une meilleure expérience TV',
      ctaTitle2: 'commence ici.',
      ctaCta: 'Essayer gratuitement 7 jours',
      ctaMicro: 'Activez en minutes et essayez toutes les fonctionnalités.',
      platforms: 'PLATEFORMES COMPATIBLES',
      epgComplete: 'GUIDE EPG COMPLET',
      languages: 'LANGUES DISPONIBLES',
      activation: 'ACTIVATION RAPIDE',
    },
    activation: {
      title: 'Activez votre appareil',
      subtitle:
        'Abonnez-vous pour débloquer le lecteur Moova sur votre Smart TV',
      step1: 'Saisissez l\'adresse MAC affichée sur votre TV',
      step2: 'Choisissez un plan qui vous convient',
      step3: 'Abonnez-vous — votre TV est activée instantanément',
      planLifetime: 'À vie',
      planAnnual: 'Annuel',
      planMonthly: 'Mensuel',
      choosePlan: 'Choisissez votre plan',
      choosePlanSub: "Sélectionnez l'option qui vous convient le mieux",
      macLabel: 'Adresse MAC',
      checking: 'Vérification de l\'appareil...',
      found: 'Appareil trouvé — choisissez un plan pour activer',
      alreadyPaid: 'Cet appareil est déjà activé. Aucun paiement nécessaire.',
      notFound:
        "Non trouvé. Installez et ouvrez d'abord l'application sur votre TV.",
      smartTv: 'Smart TV',
      securePayment: 'Paiement sécurisé',
      instantActivation: 'Activation instantanée',
      acceptTerms: "J'accepte les",
      termsLink: "conditions d'utilisation et la politique de confidentialité",
      processing: 'Traitement...',
      activateBtn: 'Activer',
      activateDevice: "Activer l'appareil",
      disclaimer:
        "L'application n'inclut pas de playlists ni de chaînes. Configurez votre propre playlist après l'activation.",
      popular: 'Populaire',
      oneTime: 'Paiement unique',
      perYear: 'Par an',
      perMonth: 'Par mois',
      errorPayment:
        'Impossible de générer le lien de paiement. Veuillez réessayer.',
      errorGeneric:
        'Erreur lors du traitement du paiement. Veuillez réessayer.',
      macFound: 'Trouvé',
      descLifetime: 'Payez une fois, utilisez pour toujours',
      descAnnual: 'Renouvellement annuel',
      descMonthly: 'Renouvellement mensuel',
      alreadyPaidUntil: 'Cet appareil a déjà un plan actif jusqu’au {date}. Aucun paiement nécessaire.',
      alreadyLifetime: 'Cet appareil a déjà le plan À vie. Aucun paiement nécessaire.',
    },
    playlist: {
      title: 'Envoyer une Playlist',
      subtitle:
        "Chargez votre propre playlist dans l'application. Supporte M3U, M3U8 et d'autres sources.",
      step1: "Saisissez l'adresse MAC de votre TV",
      step2: "Choisissez une méthode d'envoi et fournissez la playlist",
      step3: 'Envoyez — la playlist se synchronise instantanément avec votre TV',
      multipleFormats: 'Formats multiples',
      multipleFormatsDesc:
        'Envoi par URL, fichier, identifiants du serveur ou lien M3U8 direct.',
      epgSupport: 'Support EPG',
      epgSupportDesc:
        'Ajoutez une URL de guide électronique des programmes pour les grilles de chaînes.',
      pinProtection: 'Protection par PIN',
      pinProtectionDesc:
        'Protégez optionnellement votre playlist avec un PIN à 4 chiffres.',
      macLabel: 'Adresse MAC',
      checking: "Vérification de l'appareil...",
      found: 'Appareil trouvé — vous pouvez envoyer votre playlist',
      notFound:
        "Non trouvé. Installez et ouvrez d'abord l'application sur votre TV.",
      playlistName: 'Nom de la Playlist',
      playlistPlaceholder: 'Ma Playlist',
      uploadMethod: "Méthode d'envoi",
      urlTab: 'URL',
      fileTab: 'Fichier',
      hostTab: 'Host',
      m3u8Tab: 'M3U8',
      m3uUrl: 'URL M3U',
      m3u8Url: 'URL M3U8',
      m3u8Note: "Pour les listes M3U8 d'autres sources",
      m3uFile: 'Fichier M3U/M3U8',
      hostLabel: 'Host',
      usernameLabel: "Nom d'utilisateur",
      passwordLabel: 'Mot de passe',
      options: 'Options',
      epgUrl: 'URL EPG (optionnel)',
      protectPin: 'Protéger la playlist par PIN',
      pinPlaceholder: 'PIN à 4 chiffres',
      setDefault: 'Définir comme playlist par défaut',
      uploading: 'Envoi en cours...',
      uploadBtn: 'Envoyer la Playlist',
      uploadSuccess: 'Playlist envoyée avec succès !',
      uploadError:
        "Erreur lors de l'envoi de la playlist. Vérifiez le MAC et réessayez.",
      fileError: 'Seuls les fichiers .m3u et .m3u8 sont pris en charge',
    },
    reseller: {
      title: 'Devenir Revendeur',
      subtitle:
        'Rejoignez notre programme partenaire et commencez à gagner avec les activations MoovaTV.',
      step1: 'Créez votre compte revendeur',
      step2: "Accédez au tableau de bord et générez des codes d'activation",
      step3: 'Vendez à vos clients et gagnez des commissions',
      manageClients: 'Gérez vos clients',
      manageClientsDesc:
        'Tableau de bord complet pour suivre les activations, clients et revenus.',
      competitivePricing: 'Tarifs compétitifs',
      competitivePricingDesc:
        'Achetez des crédits en gros à prix réduit.',
      realTimeAnalytics: 'Analyses en temps réel',
      realTimeAnalyticsDesc:
        'Suivez vos revenus, appareils actifs et croissance.',
      createAccount: 'Créez votre compte',
      fieldsRequired: 'Les champs marqués d\'un * sont obligatoires',
      nameLabel: 'Prénom',
      namePlaceholder: 'Prénom',
      surnameLabel: 'Nom',
      surnamePlaceholder: 'Nom de famille',
      emailLabel: 'E-mail',
      emailPlaceholder: 'vous@email.com',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: '6 caractères minimum',
      confirmLabel: 'Confirmer',
      confirmPlaceholder: 'Répétez le mot de passe',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      optional: 'Facultatif',
      companyLabel: 'Entreprise',
      companyPlaceholder: "Nom de l'entreprise",
      phoneLabel: 'Téléphone',
      countryLabel: 'Pays',
      countryPlaceholder: 'France',
      countrySelect: 'Choisissez votre pays',
      countrySearch: 'Rechercher un pays...',
      countryNone: 'Aucun pays trouvé',
      phoneInvalid: 'Numéro de téléphone invalide pour le pays choisi',
      dialCode: 'Indicatif du pays',
      partnerType: 'Type de partenaire',
      resellerOption: 'Revendeur',
      resellerDesc: 'Acheter et vendre des activations',
      referralOption: 'Parrainage',
      referralDesc: 'Gagner par parrainage',
      creatingAccount: 'Création du compte...',
      createBtn: 'Créer un compte revendeur',
      registerSuccess: 'Inscription réussie ! Redirection...',
      registerError: "Échec de l'inscription. Veuillez réessayer.",
    },
    pricing: {
      title: 'Des tarifs simples et transparents',
      subtitle:
        "Choisissez le plan qui vous convient. Activation unique, sans abonnement.",
    },
  },

  // ─────────────────────────────────────────────
  // SPANISH
  // ─────────────────────────────────────────────
  es: {
    nav: {
      home: 'Inicio',
      activation: 'Activación',
      playlist: 'Playlist',
      reseller: 'Revendedor',
      pricing: 'Precios',
      skipToContent: 'Saltar al contenido',
      primary: 'Principal',
      language: 'Idioma',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
    },
    footer: {
      tagline: 'Más control, organización y seguridad para tu experiencia en la TV.',
      product: 'Producto',
      partners: 'Socios',
      becomeReseller: 'Conviértete en Revendedor',
      legal: 'Legal',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Uso',
      copyright: 'Todos los derechos reservados.',
      disclaimer:
        'MoovaTV es exclusivamente un reproductor multimedia. No vendemos, distribuimos ni proporcionamos listas, canales o contenido de ningún tipo.',
    },
    home: {
      metaTitle: 'MoovaTV — Reproductor multimedia para Smart TVs con control desde el celular',
      metaDesc: 'Organiza tu catálogo, controla la TV desde el celular y mira con más seguridad en Smart TVs compatibles. Prueba gratis por 7 días.',
      heroTitle1: 'Toma el control de tu TV con un reproductor',
      heroTitle2: 'más inteligente.',
      heroSub:
        'Organiza tu catálogo, controla la TV desde el celular y mira con más seguridad en Smart TVs compatibles.',
      heroCta: 'Probar gratis por 7 días',
      heroPlans: 'Ver planes',
      heroMicro: 'Activación rápida. Sin tarjeta en la prueba gratis.',
      marqueeLabel: 'Compatible con las principales plataformas y marcas de Smart TV.',
      featuresLabel: 'Características',
      featuresTitle: 'Todo lo que necesitas para ver con más control.',
      featuresSub: 'Más comodidad, mejor organización y mayor seguridad para tu experiencia.',
      feat1Title: 'Controla todo desde el celular',
      feat1Desc:
        'Explora el catálogo, envía a la TV y controla la reproducción en tiempo real desde la segunda pantalla.',
      feat2Title: 'Más seguridad para tu hogar',
      feat2Desc:
        'Usa PIN, modo infantil y filtros de navegación para más control sobre el acceso al contenido.',
      feat3Title: 'Reproducción más completa',
      feat3Desc:
        'Subtítulos, múltiples pistas de audio y calidad adaptativa para una experiencia más estable y cómoda.',
      feat4Title: 'Catálogo más organizado',
      feat4Desc:
        'Visual más rico con portadas, sinopsis e información que te ayuda a encontrar qué ver más rápido.',
      syncLabel: 'Segunda Pantalla',
      syncTitle1: 'Elige en el celular.',
      syncTitle2: 'Mira en la TV.',
      syncSub: 'Navega con más agilidad, envía contenido a la TV y controla todo sin depender de un control remoto confuso.',
      syncMicro: 'Sincronización en tiempo real',
      syncStep0: 'Navegando por el catálogo',
      syncStep1: 'Eligiendo película',
      syncStep2: 'Viendo detalles',
      syncStep3: 'Enviando a la TV',
      syncStep4: 'Reproduciendo en la TV',
      syncStep5: 'Móvil como mando',
      syncStep6: 'Pausado desde el móvil',
      remoteNowPlaying: 'Reproduciendo',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'Pausado',
      syncWatchOnTv: 'Ver en la TV',
      syncSentToTv: 'Enviado a la TV',
      syncTvWaiting: 'Esperando',
      numbersSub: 'Amplia compatibilidad y configuración rápida para empezar sin complicaciones.',
      stepsTitle: 'Empieza en pocos minutos',
      stepsSub: 'Activa tu TV, añade tu contenido y controla todo desde tu celular.',
      step: 'Paso',
      step1Title: 'Activa tu TV',
      step1Desc:
        'Escanea el código QR en pantalla o ingresa el código de activación. El proceso toma menos de 30 segundos.',
      step2Title: 'Añade tu contenido',
      step2Desc:
        'Importa tu playlist y mantén todo organizado por categoría, calidad y género.',
      step3Title: 'Mira y controla',
      step3Desc:
        'Explora el catálogo en tu celular, envía a la TV y gestiona la reproducción en tiempo real.',
      pricingTitle: 'Elige el plan ideal para usar el reproductor',
      pricingSub:
        'Pagas por el acceso al reproductor y las funcionalidades de la plataforma. MoovaTV no comercializa listas, canales ni contenido.',
      planFreeName: 'Prueba gratis',
      planFreeInfo: 'Prueba la plataforma por 7 días',
      planFreePrice: 'Gratis',
      planFreeFeat1: 'Acceso completo durante el período de prueba',
      planFreeFeat2: 'Todas las funcionalidades desbloqueadas',
      planFreeFeat3: 'Sin tarjeta de crédito',
      planFreeCta: 'Empezar gratis',
      planAnnualName: 'Anual',
      planAnnualInfo: 'Acceso por 12 meses',
      planAnnualDetail: '/ año',
      planAnnualFeat1: 'Reproductor completo sin restricciones',
      planAnnualFeat2: 'Control desde el celular',
      planAnnualFeat3: 'Seguridad con PIN',
      planAnnualFeat4: 'Catálogo organizado y enriquecido',
      planAnnualFeat5: 'Interfaz en 7 idiomas',
      planAnnualFeat6: 'Soporte prioritario',
      planAnnualCta: 'Suscribirse anual',
      planLifetimeName: 'Lifetime',
      planGoldName: 'Gold Lifetime',
      planGoldInfo: 'Pago único',
      planGoldDetail: 'para siempre',
      planGoldBadge: 'Mejor relación calidad-precio',
      planGoldSavings: 'Mejor relación calidad-precio',
      planGoldFeat1: 'Todo del plan Anual',
      planGoldFeat2: 'Acceso de por vida al reproductor',
      planGoldFeat3: 'Sin renovación',
      planGoldFeat4: 'Actualizaciones futuras incluidas',
      planGoldFeat5: 'Soporte prioritario',
      planGoldCta: 'Activar ahora',
      ctaTitle1: 'Una mejor experiencia en tu TV',
      ctaTitle2: 'comienza aquí.',
      ctaCta: 'Probar gratis por 7 días',
      ctaMicro: 'Activa en minutos y prueba todas las funcionalidades.',
      platforms: 'PLATAFORMAS COMPATIBLES',
      epgComplete: 'GUÍA EPG COMPLETA',
      languages: 'IDIOMAS DISPONIBLES',
      activation: 'ACTIVACIÓN RÁPIDA',
    },
    activation: {
      title: 'Activa tu dispositivo',
      subtitle:
        'Suscríbete para desbloquear el reproductor Moova en tu Smart TV',
      step1: 'Ingresa la dirección MAC que aparece en tu TV',
      step2: 'Elige el plan que más te convenga',
      step3: 'Suscríbete — tu TV se activa al instante',
      planLifetime: 'De por vida',
      planAnnual: 'Anual',
      planMonthly: 'Mensual',
      choosePlan: 'Elige tu plan',
      choosePlanSub: 'Selecciona la opción que mejor se adapte a ti',
      macLabel: 'Dirección MAC',
      checking: 'Verificando dispositivo...',
      found: 'Dispositivo encontrado — elige un plan para activar',
      alreadyPaid: 'Este dispositivo ya está activado. No se necesita pago.',
      notFound:
        'No encontrado. Instala y abre la app en tu TV primero.',
      smartTv: 'Smart TV',
      securePayment: 'Pago seguro',
      instantActivation: 'Activación instantánea',
      acceptTerms: 'Acepto los',
      termsLink: 'términos de uso y la política de privacidad',
      processing: 'Procesando...',
      activateBtn: 'Activar',
      activateDevice: 'Activar Dispositivo',
      disclaimer:
        'La app no incluye playlists ni canales. Configura tu propia playlist después de la activación.',
      popular: 'Popular',
      oneTime: 'Pago único',
      perYear: 'Por año',
      perMonth: 'Por mes',
      errorPayment:
        'No se pudo generar el enlace de pago. Por favor, inténtalo de nuevo.',
      errorGeneric:
        'Error al procesar el pago. Por favor, inténtalo de nuevo.',
      macFound: 'Encontrado',
      descLifetime: 'Paga una vez, úsalo para siempre',
      descAnnual: 'Renovación anual',
      descMonthly: 'Renovación mensual',
      alreadyPaidUntil: 'Este dispositivo ya tiene un plan activo hasta el {date}. No se necesita pago.',
      alreadyLifetime: 'Este dispositivo ya tiene el plan De por vida. No se necesita pago.',
    },
    playlist: {
      title: 'Subir Playlist',
      subtitle:
        'Carga tu propia playlist en la app. Compatible con M3U, M3U8 y otras fuentes.',
      step1: 'Ingresa la dirección MAC de tu dispositivo TV',
      step2: 'Elige un método de carga y proporciona la playlist',
      step3: 'Envía — la playlist se sincroniza con tu TV al instante',
      multipleFormats: 'Múltiples formatos',
      multipleFormatsDesc:
        'Sube vía URL, archivo, credenciales del servidor o enlace M3U8 directo.',
      epgSupport: 'Soporte EPG',
      epgSupportDesc:
        'Añade una URL de Guía Electrónica de Programación para horarios de canales.',
      pinProtection: 'Protección con PIN',
      pinProtectionDesc:
        'Protege opcionalmente tu playlist con un PIN de 4 dígitos.',
      macLabel: 'Dirección MAC',
      checking: 'Verificando dispositivo...',
      found: 'Dispositivo encontrado — puedes subir tu playlist',
      notFound:
        'No encontrado. Instala y abre la app en tu TV primero.',
      playlistName: 'Nombre de la Playlist',
      playlistPlaceholder: 'Mi Playlist',
      uploadMethod: 'Método de carga',
      urlTab: 'URL',
      fileTab: 'Archivo',
      hostTab: 'Host',
      m3u8Tab: 'M3U8',
      m3uUrl: 'URL M3U',
      m3u8Url: 'URL M3U8',
      m3u8Note: 'Para listas M3U8 de otras fuentes',
      m3uFile: 'Archivo M3U/M3U8',
      hostLabel: 'Host',
      usernameLabel: 'Usuario',
      passwordLabel: 'Contraseña',
      options: 'Opciones',
      epgUrl: 'URL del EPG (opcional)',
      protectPin: 'Proteger playlist con PIN',
      pinPlaceholder: 'PIN de 4 dígitos',
      setDefault: 'Establecer como playlist predeterminada',
      uploading: 'Subiendo...',
      uploadBtn: 'Subir Playlist',
      uploadSuccess: '¡Playlist subida con éxito!',
      uploadError:
        'Error al subir la playlist. Verifica el MAC e inténtalo de nuevo.',
      fileError: 'Solo se admiten archivos .m3u y .m3u8',
    },
    reseller: {
      title: 'Conviértete en Revendedor',
      subtitle:
        'Únete a nuestro programa de socios y empieza a ganar con activaciones MoovaTV.',
      step1: 'Crea tu cuenta de revendedor',
      step2: 'Accede al panel y genera códigos de activación',
      step3: 'Vende a tus clientes y gana comisiones',
      manageClients: 'Gestiona tus clientes',
      manageClientsDesc:
        'Panel completo para seguir activaciones, clientes e ingresos.',
      competitivePricing: 'Precios competitivos',
      competitivePricingDesc:
        'Compra créditos en volumen a precios con descuento.',
      realTimeAnalytics: 'Análisis en tiempo real',
      realTimeAnalyticsDesc:
        'Sigue tus ingresos, dispositivos activos y crecimiento.',
      createAccount: 'Crea tu cuenta',
      fieldsRequired: 'Los campos marcados con * son obligatorios',
      nameLabel: 'Nombre',
      namePlaceholder: 'Nombre',
      surnameLabel: 'Apellido',
      surnamePlaceholder: 'Apellido',
      emailLabel: 'Email',
      emailPlaceholder: 'tu@email.com',
      passwordLabel: 'Contraseña',
      passwordPlaceholder: 'Mínimo 6 caracteres',
      confirmLabel: 'Confirmar',
      confirmPlaceholder: 'Repite la contraseña',
      passwordMismatch: 'Las contraseñas no coinciden',
      optional: 'Opcional',
      companyLabel: 'Empresa',
      companyPlaceholder: 'Nombre de la empresa',
      phoneLabel: 'Teléfono',
      countryLabel: 'País',
      countryPlaceholder: 'España',
      countrySelect: 'Selecciona tu país',
      countrySearch: 'Buscar país...',
      countryNone: 'Ningún país encontrado',
      phoneInvalid: 'Teléfono no válido para el país seleccionado',
      dialCode: 'Código del país',
      partnerType: 'Tipo de socio',
      resellerOption: 'Revendedor',
      resellerDesc: 'Comprar y vender activaciones',
      referralOption: 'Referido',
      referralDesc: 'Ganar por referido',
      creatingAccount: 'Creando cuenta...',
      createBtn: 'Crear Cuenta de Revendedor',
      registerSuccess: '¡Registro exitoso! Redirigiendo...',
      registerError: 'Error en el registro. Por favor, inténtalo de nuevo.',
    },
    pricing: {
      title: 'Precios simples y transparentes',
      subtitle:
        'Elige el plan que mejor te funcione. Activación única, sin suscripciones.',
    },
  },

  // ─────────────────────────────────────────────
  // UKRAINIAN
  // ─────────────────────────────────────────────
  uk: {
    lp: {
      menuFeatures: 'Можливості',
      getStarted: 'Почати',
      heroEyebrow: 'Медіаплатформа для Smart TV',
      heroH1a: 'Розумніше керування телевізором',
      heroH1b: 'для перегляду',
      heroH1c: 'нового покоління.',
      heroLead: 'Упорядкуйте каталог, керуйте телевізором з телефона й дивіться безпечніше та зручніше.',
      heroTry: 'Спробуйте безкоштовно 7 днів →',
      heroPlans: 'Переглянути плани',
      trust1: 'Швидка активація',
      trust2: 'Працює на Smart TV',
      trust3: 'Безпечно та приватно',
      compatTitle: 'Сумісно з провідними платформами Smart TV',
      moreName: 'та інші',
      moreSub: 'Платформи',
      featKicker: 'Потужні можливості',
      featTitleA: 'Усе необхідне,',
      featTitleB: 'створене для кращого телебачення.',
      learn: 'Дізнатися більше →',
      f1t: 'Керуйте з телефона',
      f1d: 'Використовуйте смартфон як пульт. Швидко, інтуїтивно й завжди під рукою.',
      f2t: 'Безпечніше для вашої родини',
      f2d: 'Захист PIN-кодом, фільтри контенту та батьківський контроль, щоб усі були в безпеці.',
      f3t: 'Потужне відтворення',
      f3d: 'Плавна робота, підтримка багатьох форматів і преміальна якість трансляції.',
      f4t: 'Упорядкований каталог',
      f4d: 'Імпортуйте, сортуйте та керуйте своїм контентом за зручними категоріями.',
      stat1: 'Сумісних платформ',
      stat2: 'Підтримуваних мов',
      stat3: 'Середній час активації',
      stat4: 'Преміальна підтримка',
      setupKicker: 'Просте налаштування',
      setupTitle: 'Почніть за 3 простих кроки',
      step1t: 'Активуйте телевізор',
      step1d: 'Відскануйте QR-код або введіть код активації на телевізорі, щоб підключитися за кілька секунд.',
      step2t: 'Додайте свій контент',
      step2d: 'Імпортуйте плейлисти або медіаджерела та впорядкуйте їх на свій смак.',
      step3t: 'Дивіться та керуйте',
      step3d: 'Насолоджуйтеся контентом і керуйте всім з телефона — будь-коли й будь-де.',
      priceKicker: 'Ціни',
      priceTitle: 'Оберіть план, який вам підходить',
      priceSub: 'Одна платформа. Усі ваші розваги. На ваш розсуд.',
      annualName: 'Річний',
      annualDesc: 'Доступ на 12 місяців',
      annualUnit: '/рік',
      annualF1: 'Повний плеєр без обмежень',
      annualF2: 'Підтримка плейлистів',
      annualF3: 'Батьківський контроль',
      annualF4: 'Захист PIN-кодом',
      annualF5: 'Упорядкований каталог',
      annualCta: 'Обрати річний план',
      bestValue: 'Найвигідніше',
      lifeName: 'Назавжди',
      lifeDesc: 'Одноразовий платіж',
      lifeUnit: 'назавжди',
      lifeF1: 'Усе з річного плану',
      lifeF2: 'Довічний доступ',
      lifeF3: 'Майбутні оновлення включено',
      lifeF4: 'Без поновлення',
      lifeF5: 'Пріоритетна підтримка',
      lifeCta: 'Отримати довічний доступ',
      ben1: '7 днів безкоштовно',
      ben2: 'Безпечна оплата',
      ben3: 'Скасування будь-коли',
      ctaTitle: 'Майбутнє телебачення — у ваших руках.',
      ctaSub: 'Приєднуйтеся до тисяч користувачів, які насолоджуються розумнішими, безпечнішими та потужнішими розвагами з MoovaTV.',
      ctaBtn: 'Спробуйте MoovaTV безкоштовно 7 днів →',
      pnpKicker: 'Плеєр, а не провайдер',
      pnpTitleA: 'Ми продаємо плеєр.',
      pnpTitleB: 'Ніколи не контент.',
      pnpLead: 'MoovaTV постачається без каналів, плейлистів і підписок на контент. Ви додаєте власні джерела, а ми робимо так, щоб вони виглядали преміально на вашому телевізорі.',
      pnpYesT: 'Що ви отримуєте',
      pnpYes1: 'Преміальний плеєр для ваших власних плейлистів і медіаджерел',
      pnpYes2: 'EPG, обране, батьківський контроль і синхронізація між пристроями',
      pnpYes3: 'Телефон як пульт на Tizen, webOS, Roku, Google TV та інших',
      pnpNoT: 'Чого ми ніколи не продаємо',
      pnpNo1: 'Жодних телеканалів чи прямих трансляцій',
      pnpNo2: 'Жодних плейлистів, пакетів чи підписок на контент',
      pnpNo3: 'Жодного хостингу контенту: ваші джерела залишаються вашими',
      footTagline: 'Розумне керування Smart TV для перегляду нового покоління.',
      footProduct: 'Продукт',
      footFeatures: 'Можливості',
      footActivation: 'Активація',
      footPlaylist: 'Плейлист',
      footDevices: 'Пристрої',
      footCompany: 'Компанія',
      footContact: 'Контакти',
      footAbout: 'Про нас',
      footReseller: 'Реселер',
      footBecome: 'Стати реселером',
      footLogin: 'Вхід для реселерів',
      footLegal: 'Правова інформація',
      footPrivacy: 'Політика конфіденційності',
      footTerms: 'Умови використання',
      footCopy: '© 2026 MoovaTV. Усі права захищено. · Безпечно · Приватно · Надійно',
    },
    nav: {
      home: 'Головна',
      activation: 'Активація',
      playlist: 'Плейлист',
      reseller: 'Реселер',
      pricing: 'Ціни',
      skipToContent: 'Перейти до вмісту',
      primary: 'Основна',
      language: 'Мова',
      openMenu: 'Відкрити меню',
      closeMenu: 'Закрити меню',
    },
    footer: {
      tagline: 'Більше контролю, порядку та безпеки для вашого перегляду.',
      product: 'Продукт',
      partners: 'Партнери',
      becomeReseller: 'Стати реселером',
      legal: 'Правова інформація',
      privacy: 'Політика конфіденційності',
      terms: 'Умови використання',
      copyright: 'Усі права захищено.',
      disclaimer:
        'MoovaTV — це виключно медіаплеєр. Ми не продаємо, не розповсюджуємо й не надаємо плейлисти, канали чи будь-який інший контент.',
    },
    home: {
      metaTitle: 'MoovaTV — Медіаплеєр для Smart TV з керуванням із телефона',
      metaDesc: 'Упорядкуйте каталог, керуйте телевізором з телефона й дивіться безпечніше на сумісних Smart TV. 7 днів безкоштовно.',
      heroTitle1: 'Візьміть телевізор під контроль із розумнішим',
      heroTitle2: 'медіаплеєром.',
      heroSub:
        'Упорядкуйте каталог, керуйте телевізором з телефона й дивіться безпечніше на сумісних Smart TV.',
      heroCta: 'Спробуйте безкоштовно 7 днів',
      heroPlans: 'Переглянути плани',
      heroMicro: 'Швидка активація. Без кредитної картки для пробного періоду.',
      marqueeLabel: 'Сумісно з провідними платформами та брендами Smart TV.',
      featuresLabel: 'Можливості',
      featuresTitle: 'Усе необхідне, щоб дивитися з більшим контролем.',
      featuresSub: 'Більше комфорту, кращий порядок і надійніша безпека для вашого перегляду.',
      feat1Title: 'Керуйте всім з телефона',
      feat1Desc:
        'Переглядайте каталог, надсилайте на телевізор і керуйте відтворенням у реальному часі через другий екран.',
      feat2Title: 'Більше безпеки для вашого дому',
      feat2Desc:
        'Використовуйте PIN-код, дитячий режим і фільтри перегляду, щоб краще контролювати доступ до контенту.',
      feat3Title: 'Повніше відтворення',
      feat3Desc:
        'Субтитри, кілька звукових доріжок і адаптивна якість для стабільнішого й комфортнішого перегляду.',
      feat4Title: 'Краще впорядкований каталог',
      feat4Desc:
        'Насичене оформлення з обкладинками, описами та інформацією, що допомагає швидше знайти, що подивитися.',
      syncLabel: 'Другий екран',
      syncTitle1: 'Обирайте на телефоні.',
      syncTitle2: 'Дивіться на телевізорі.',
      syncSub: 'Переглядайте швидше, надсилайте контент на телевізор і керуйте всім без незручного пульта.',
      syncMicro: 'Синхронізація в реальному часі',
      syncStep0: 'Перегляд каталогу',
      syncStep1: 'Вибір фільму',
      syncStep2: 'Перегляд деталей',
      syncStep3: 'Надсилання на телевізор',
      syncStep4: 'Відтворення на телевізорі',
      syncStep5: 'Телефон як пульт',
      syncStep6: 'Пауза з телефона',
      remoteNowPlaying: 'Зараз відтворюється',
      remoteSampleTitle: 'Inception',
      tvPausedLabel: 'Пауза',
      syncWatchOnTv: 'Дивитися на телевізорі',
      syncSentToTv: 'Надіслано на телевізор',
      syncTvWaiting: 'Очікування',
      numbersSub: 'Широка сумісність і швидке налаштування, щоб почати без зайвого клопоту.',
      stepsTitle: 'Почніть за кілька хвилин',
      stepsSub: 'Активуйте телевізор, додайте контент і керуйте всім з телефона.',
      step: 'Крок',
      step1Title: 'Активуйте телевізор',
      step1Desc:
        'Відскануйте QR-код на екрані або введіть код активації. Процес займає менше 30 секунд.',
      step2Title: 'Додайте свій контент',
      step2Desc:
        'Імпортуйте плейлист і тримайте все впорядкованим за категорією, якістю та жанром.',
      step3Title: 'Дивіться та керуйте',
      step3Desc:
        'Переглядайте каталог на телефоні, надсилайте на телевізор і керуйте відтворенням у реальному часі.',
      pricingTitle: 'Оберіть відповідний план для плеєра',
      pricingSub:
        'Ви платите за доступ до плеєра та функцій платформи. MoovaTV не продає плейлисти, канали чи контент.',
      planFreeName: 'Безкоштовний пробний період',
      planFreeInfo: 'Спробуйте платформу 7 днів',
      planFreePrice: 'Безкоштовно',
      planFreeFeat1: 'Повний доступ протягом пробного періоду',
      planFreeFeat2: 'Усі функції розблоковано',
      planFreeFeat3: 'Без кредитної картки',
      planFreeCta: 'Почати безкоштовно',
      planAnnualName: 'Річний',
      planAnnualInfo: 'Доступ на 12 місяців',
      planAnnualDetail: '/ рік',
      planAnnualFeat1: 'Повний плеєр без обмежень',
      planAnnualFeat2: 'Керування з телефона',
      planAnnualFeat3: 'Захист PIN-кодом',
      planAnnualFeat4: 'Упорядкований і збагачений каталог',
      planAnnualFeat5: 'Інтерфейс 7 мовами',
      planAnnualFeat6: 'Пріоритетна підтримка',
      planAnnualCta: 'Оформити річний план',
      planLifetimeName: 'Назавжди',
      planGoldName: 'Довічний Gold',
      planGoldInfo: 'Одноразовий платіж',
      planGoldDetail: 'назавжди',
      planGoldBadge: 'Найвигідніше',
      planGoldSavings: 'Найвигідніше',
      planGoldFeat1: 'Усе з річного плану',
      planGoldFeat2: 'Довічний доступ до плеєра',
      planGoldFeat3: 'Без поновлення',
      planGoldFeat4: 'Майбутні оновлення включено',
      planGoldFeat5: 'Пріоритетна підтримка',
      planGoldCta: 'Активувати зараз',
      ctaTitle1: 'Кращий перегляд телебачення',
      ctaTitle2: 'починається тут.',
      ctaCta: 'Спробуйте безкоштовно 7 днів',
      ctaMicro: 'Активуйте за кілька хвилин і спробуйте всі функції.',
      platforms: 'СУМІСНІ ПЛАТФОРМИ',
      epgComplete: 'ПОВНИЙ ТЕЛЕГІД EPG',
      languages: 'ДОСТУПНІ МОВИ',
      activation: 'ШВИДКА АКТИВАЦІЯ',
    },
    activation: {
      title: 'Активуйте свій пристрій',
      subtitle: 'Оформіть підписку, щоб розблокувати плеєр Moova на вашому Smart TV',
      step1: 'Введіть MAC-адресу, показану на телевізорі',
      step2: 'Оберіть план, який вам підходить',
      step3: 'Оформіть підписку — телевізор активується миттєво',
      planLifetime: 'Назавжди',
      planAnnual: 'Річний',
      planMonthly: 'Місячний',
      choosePlan: 'Оберіть свій план',
      choosePlanSub: 'Виберіть варіант, який вам найбільше підходить',
      macLabel: 'MAC-адреса',
      checking: 'Перевіряємо пристрій...',
      found: 'Пристрій знайдено — оберіть план для активації',
      alreadyPaid: 'Цей пристрій уже активовано. Оплата не потрібна.',
      notFound: 'Не знайдено. Спершу встановіть і відкрийте застосунок на телевізорі.',
      smartTv: 'Smart TV',
      securePayment: 'Безпечна оплата',
      instantActivation: 'Миттєва активація',
      acceptTerms: 'Я приймаю',
      termsLink: 'умови використання та політику конфіденційності',
      processing: 'Обробка...',
      activateBtn: 'Активувати',
      activateDevice: 'Активувати пристрій',
      disclaimer:
        'Застосунок не містить плейлистів чи каналів. Налаштуйте власний плейлист після активації.',
      popular: 'Популярний',
      oneTime: 'Одноразовий платіж',
      perYear: 'На рік',
      perMonth: 'На місяць',
      errorPayment: 'Не вдалося створити посилання для оплати. Спробуйте ще раз.',
      errorGeneric: 'Помилка обробки платежу. Спробуйте ще раз.',
      macFound: 'Знайдено',
      descLifetime: 'Платите один раз, користуєтеся назавжди',
      descAnnual: 'Поновлюється щороку',
      descMonthly: 'Поновлюється щомісяця',
      alreadyPaidUntil: 'Цей пристрій уже має активний план до {date}. Оплата не потрібна.',
      alreadyLifetime: 'Цей пристрій уже має довічний план. Оплата не потрібна.',
    },
    playlist: {
      title: 'Завантажити плейлист',
      subtitle:
        'Завантажте власний плейлист у застосунок. Підтримуються M3U, M3U8 та інші джерела.',
      step1: 'Введіть MAC-адресу вашого телевізора',
      step2: 'Оберіть спосіб завантаження та вкажіть плейлист',
      step3: 'Надішліть — плейлист миттєво синхронізується з телевізором',
      multipleFormats: 'Кілька форматів',
      multipleFormatsDesc:
        'Завантажуйте за URL, файлом, обліковими даними сервера або прямим посиланням M3U8.',
      epgSupport: 'Підтримка EPG',
      epgSupportDesc:
        'Додайте URL електронного телегіда для розкладу каналів.',
      pinProtection: 'Захист PIN-кодом',
      pinProtectionDesc:
        'За бажанням захистіть плейлист 4-значним PIN-кодом.',
      macLabel: 'MAC-адреса',
      checking: 'Перевіряємо пристрій...',
      found: 'Пристрій знайдено — можете завантажити плейлист',
      notFound: 'Не знайдено. Спершу встановіть і відкрийте застосунок на телевізорі.',
      playlistName: 'Назва плейлиста',
      playlistPlaceholder: 'Мій плейлист',
      uploadMethod: 'Спосіб завантаження',
      urlTab: 'URL',
      fileTab: 'Файл',
      hostTab: 'Хост',
      m3u8Tab: 'M3U8',
      m3uUrl: 'URL M3U',
      m3u8Url: 'URL M3U8',
      m3u8Note: 'Для списків M3U8 з інших джерел',
      m3uFile: 'Файл M3U/M3U8',
      hostLabel: 'Хост',
      usernameLabel: 'Ім’я користувача',
      passwordLabel: 'Пароль',
      options: 'Параметри',
      epgUrl: 'URL EPG (необов’язково)',
      protectPin: 'Захистити плейлист PIN-кодом',
      pinPlaceholder: '4-значний PIN-код',
      setDefault: 'Зробити плейлистом за замовчуванням',
      uploading: 'Завантаження...',
      uploadBtn: 'Завантажити плейлист',
      uploadSuccess: 'Плейлист успішно завантажено!',
      uploadError: 'Помилка завантаження плейлиста. Перевірте MAC-адресу та спробуйте ще раз.',
      fileError: 'Підтримуються лише файли .m3u та .m3u8',
    },
    reseller: {
      title: 'Стати реселером',
      subtitle:
        'Приєднуйтеся до нашої партнерської програми та заробляйте на активаціях MoovaTV.',
      step1: 'Створіть обліковий запис реселера',
      step2: 'Увійдіть у панель і створюйте коди активації',
      step3: 'Продавайте клієнтам і отримуйте комісію',
      manageClients: 'Керуйте клієнтами',
      manageClientsDesc:
        'Повна панель для відстеження активацій, клієнтів і доходів.',
      competitivePricing: 'Конкурентні ціни',
      competitivePricingDesc:
        'Купуйте кредити оптом за зниженими цінами.',
      realTimeAnalytics: 'Аналітика в реальному часі',
      realTimeAnalyticsDesc:
        'Відстежуйте дохід, активні пристрої та зростання.',
      createAccount: 'Створіть обліковий запис',
      fieldsRequired: 'Поля, позначені *, обов’язкові',
      nameLabel: 'Ім’я',
      namePlaceholder: 'Ім’я',
      surnameLabel: 'Прізвище',
      surnamePlaceholder: 'Прізвище',
      emailLabel: 'Ел. пошта',
      emailPlaceholder: 'vy@email.com',
      passwordLabel: 'Пароль',
      passwordPlaceholder: 'Мін. 6 символів',
      confirmLabel: 'Підтвердження',
      confirmPlaceholder: 'Повторіть пароль',
      passwordMismatch: 'Паролі не збігаються',
      optional: 'Необов’язково',
      companyLabel: 'Компанія',
      companyPlaceholder: 'Назва компанії',
      phoneLabel: 'Телефон',
      countryLabel: 'Країна',
      countryPlaceholder: 'Країна',
      countrySelect: 'Оберіть свою країну',
      countrySearch: 'Пошук країни...',
      countryNone: 'Країну не знайдено',
      phoneInvalid: 'Неправильний номер телефону для вибраної країни',
      dialCode: 'Код країни',
      partnerType: 'Тип партнера',
      resellerOption: 'Реселер',
      resellerDesc: 'Купуйте та продавайте активації',
      referralOption: 'Реферал',
      referralDesc: 'Заробляйте за кожного запрошеного',
      creatingAccount: 'Створюємо обліковий запис...',
      createBtn: 'Створити обліковий запис реселера',
      registerSuccess: 'Реєстрація успішна! Перенаправляємо...',
      registerError: 'Не вдалося зареєструватися. Спробуйте ще раз.',
    },
    pricing: {
      title: 'Прості та прозорі ціни',
      subtitle:
        'Оберіть план, який вам підходить. Одноразова активація, без підписок.',
    },
  },
};

const SUPPORTED_LANGS: Lang[] = ['en', 'pt', 'de', 'fr', 'es', 'uk'];

/**
 * Returns the translated string for a given section, key and language.
 * Falls back to English if the key is not found.
 */
export function t(section: string, key: string, lang: Lang): string {
  return (
    translations[lang]?.[section]?.[key] ??
    translations.en?.[section]?.[key] ??
    `[${section}.${key}]`
  );
}

/**
 * Detects the user's preferred language.
 * Priority: localStorage > navigator.language > 'en'
 */
export function detectLang(): Lang {
  // URL ?lang= wins — the TV QR code carries the TV's language so the
  // activation page (and its localized prices) match what the TV shows.
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && SUPPORTED_LANGS.includes(urlLang as Lang)) {
      return urlLang as Lang;
    }
  }

  // Check localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = localStorage.getItem('moovatv-language');
    if (stored && SUPPORTED_LANGS.includes(stored as Lang)) {
      return stored as Lang;
    }
  }

  // Check browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.toLowerCase();

    // Exact match (e.g. "en", "pt", "de", "fr", "es", "uk")
    const exact = browserLang.slice(0, 2) as Lang;
    if (SUPPORTED_LANGS.includes(exact)) {
      return exact;
    }

    // Regional variants (e.g. "pt-BR" -> "pt", "es-MX" -> "es", "de-AT" -> "de", "fr-CA" -> "fr", "uk-UA" -> "uk")
    const prefix = browserLang.split('-')[0] as Lang;
    if (SUPPORTED_LANGS.includes(prefix)) {
      return prefix;
    }
  }

  return 'en';
}
